// @mui material components
import React from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Senthil Solar React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Senthil Solar React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Icons
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import SpeedIcon from "@mui/icons-material/Speed";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images - Use an existing image or add 'maintenance_bg.jpg' to src/assets/images/
// For now, reusing 'canva_banner.png' from Awards as a placeholder; replace with a maintenance-themed bg
import bgImage from "assets/images/canva_banner.png";

// Animation imports
import { useSpring, animated, useTrail } from 'react-spring';
import { useInView } from 'react-intersection-observer';

// Create animated versions of MUI components
const AnimatedGrid = animated(Grid);
const AnimatedMKBox = animated(MKBox);

function MaintenanceService() {
  // For header animation
  const [headerRef, headerInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });
  
  const headerAnimation = useSpring({
    opacity: headerInView ? 1 : 0,
    transform: headerInView ? 'translateY(0)' : 'translateY(-50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });


  // For why maintenance section
  const [whyRef, whyInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });

  const whyHeaderAnimation = useSpring({
    opacity: whyInView ? 1 : 0,
    transform: whyInView ? 'translateY(0)' : 'translateY(30px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const maintenanceServices = [
    {
      icon: <CleaningServicesIcon />,
      title: "Panel Cleaning",
      description: "Professional cleaning to remove dust, bird droppings, and debris that reduce efficiency by up to 25%."
    },
    {
      icon: <SpeedIcon />,
      title: "Performance Monitoring",
      description: "Real-time monitoring with alerts for underperformance, ensuring optimal energy generation."
    },
    {
      icon: <BuildCircleIcon />,
      title: "Repair & Replacement",
      description: "Quick repair of faulty components, inverter servicing, and replacement of damaged parts."
    },
    {
      icon: <NotificationsActiveIcon />,
      title: "Preventive Maintenance",
      description: "Regular inspections to identify potential issues before they cause system downtime."
    }
  ];

  const [servicesRef, servicesInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const servicesTrail = useTrail(maintenanceServices.length, {
    opacity: servicesInView ? 1 : 0,
    transform: servicesInView ? 'translateY(0) scale(1)' : 'translateY(50px) scale(0.95)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For AMC section
  const [amcRef, amcInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const amcAnimation = useSpring({
    opacity: amcInView ? 1 : 0,
    transform: amcInView ? 'translateY(0)' : 'translateY(50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const amcPackage = {
    title: "Comprehensive AMC",
    frequency: "Customizable (Monthly to Bi-Annual)",
    features: [
      "Regular preventive maintenance visits based on your needs",
      "Professional panel cleaning and inspection",
      "Complete system diagnostics and performance optimization",
      "Inverter servicing and cable/connection checks",
      "24/7 remote monitoring with alerts",
      "Priority breakdown support and emergency response",
      "Component replacement for wear & tear",
      "Annual health audit and detailed performance reports"
    ],
    color: "success"
  };

  const [packageRef, packageInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const packageTrail = useTrail(1, {
    opacity: packageInView ? 1 : 0,
    transform: packageInView ? 'translateY(0) scale(1)' : 'translateY(50px) scale(0.95)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For what we check section
  const [checkRef, checkInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const checkAnimation = useSpring({
    opacity: checkInView ? 1 : 0,
    transform: checkInView ? 'translateX(0)' : 'translateX(-50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const checkItems = [
    "Solar panel cleaning and inspection for cracks/damage",
    "Inverter performance diagnostics and error logs",
    "Electrical connections, cables, and earthing",
    "Mounting structure stability and corrosion check",
    "Generation data analysis and comparison",
    "Battery health check (for off-grid systems)",
    "Lightning arrestor and surge protection devices",
    "Net meter readings and grid synchronization"
  ];

  const [checkItemsRef, checkItemsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const checkTrail = useTrail(checkItems.length, {
    opacity: checkItemsInView ? 1 : 0,
    transform: checkItemsInView ? 'scale(1)' : 'scale(0.8)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For emergency and one-time sections
  const [emergencyRef, emergencyInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const emergencyAnimation = useSpring({
    opacity: emergencyInView ? 1 : 0,
    transform: emergencyInView ? 'translateY(0)' : 'translateY(50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });



  return (
    <>
      <DefaultNavbar
        routes={routes}
        transparent
        light
      />
      <MKBox
        minHeight="60vh"
        width="100%"
        sx={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container sx={{mt:6}}>
          <AnimatedGrid
            ref={headerRef}
            style={headerAnimation}
            container
            item
            xs={12}
            lg={7}
            justifyContent="center"
            mx="auto"
          >
            <MKTypography
              variant="h1"
              color="white"
              mt={-6}
              mb={1}
              sx={({ breakpoints, typography: { size } }) => ({
                textAlign: "center",
                // Responsive font sizes
                [breakpoints.down("sm")]: {
                  fontSize: size["xl"],   // very small screens
                },
                [breakpoints.between("sm", "md")]: {
                  fontSize: size["2xl"],  // tablets
                },
                [breakpoints.up("md")]: {
                  fontSize: size["3xl"],  // desktops
                },
              })}
            >
              Solar Maintenance
              <br />
              & Support
            </MKTypography>

            <MKTypography
              variant="h5"
              color="white"
              textAlign="center"
              sx={({ breakpoints, typography: { size } }) => ({
                opacity: 0.9,
                [breakpoints.down("sm")]: {
                  fontSize: size["md"],   // smaller subtitle on phones
                },
                [breakpoints.between("sm", "md")]: {
                  fontSize: size["lg"],   // tablets
                },
                [breakpoints.up("md")]: {
                  fontSize: size["xl"],   // desktops
                },
              })}
            >
              Keep Your Solar System Running at Peak Performance
            </MKTypography>
          </AnimatedGrid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={6}>
          <Container>
            <Grid container item xs={12} lg={10} mx="auto" mb={4}>
              <MKTypography variant="body1" color="text" textAlign="center">
                Regular maintenance is crucial for maximizing your solar investment. Our comprehensive 
                maintenance services ensure your solar system operates at peak efficiency throughout its 
                25+ year lifespan. With over 1000+ systems under maintenance, we have the expertise to 
                keep your solar installation performing optimally.
              </MKTypography>
            </Grid>
            
            {/* Why Regular Maintenance Matters Section */}
            <AnimatedGrid 
              ref={whyRef}
              style={whyHeaderAnimation}
              container 
              item 
              xs={12} 
              justifyContent="center" 
              mx="auto" 
              mb={4}
            >
              <MKTypography variant="h4" textAlign="center" mb={4}>
                Why Regular Maintenance Matters
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={3} ref={servicesRef}>
              {servicesTrail.map((style, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <AnimatedMKBox 
                    style={style}
                    display="flex" 
                    alignItems="flex-start" 
                    p={3} 
                    borderRadius="lg"
                    sx={{ 
                      backgroundColor: "rgba(76, 175, 80, 0.05)",
                      border: "1px solid rgba(76, 175, 80, 0.2)",
                      height: "100%"
                    }}
                  >
                    <MKBox color="success.main" mr={2}>
                      {React.cloneElement(maintenanceServices[index].icon, { fontSize: "large" })}
                    </MKBox>
                    <MKBox>
                      <MKTypography variant="h5" mb={1}>
                        {maintenanceServices[index].title}
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        {maintenanceServices[index].description}
                      </MKTypography>
                    </MKBox>
                  </AnimatedMKBox>
                </Grid>
              ))}
            </Grid>

            {/* AMC Section */}
            <AnimatedGrid 
              ref={amcRef}
              style={amcAnimation}
              container 
              item 
              xs={12} 
              justifyContent="center" 
              mx="auto" 
              mt={6}
            >
              <MKTypography variant="h4" mb={2} textAlign="center">
                Annual Maintenance Contract (AMC)<br/>
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={3} ref={packageRef}>
              {packageTrail.map((style, index) => (
                <Grid item xs={12} lg={8} key={index} mx="auto">
                  <AnimatedMKBox 
                    style={style}
                    p={3} 
                    borderRadius="lg"
                    sx={{ 
                      height: "100%",
                      border: "2px solid",
                      borderColor: `${amcPackage.color}.main`,
                      backgroundColor: `rgba(76, 175, 80, 0.05)`,
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-10px)"
                      }
                    }}
                  >
                    <MKBox textAlign="center" mb={3}>
                      <MKTypography variant="h4" color={`${amcPackage.color}.main`} mb={1}>
                        {amcPackage.title}
                      </MKTypography>
                      <MKTypography variant="button" color="text">
                        {amcPackage.frequency} Service
                      </MKTypography>
                    </MKBox>
                    <MKBox>
                      {amcPackage.features.map((feature, idx) => (
                        <MKBox display="flex" alignItems="center" mb={1.5} key={idx}>
                          <AssignmentTurnedInIcon 
                            sx={{ color: `${amcPackage.color}.main`, fontSize: 20, mr: 1 }} 
                          />
                          <MKTypography variant="body2" color="text">
                            {feature}
                          </MKTypography>
                        </MKBox>
                      ))}
                    </MKBox>
                  </AnimatedMKBox>
                </Grid>
              ))}
            </Grid>

            {/* What We Check During Maintenance Section */}
            <AnimatedGrid 
              ref={checkRef}
              style={checkAnimation}
              container 
              item 
              xs={12} 
              md={10} 
              lg={8} 
              justifyContent="center" 
              mx="auto" 
              mt={6}
            >
              <MKTypography variant="h4" mb={3}>
                What We Check During Maintenance
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={2} ref={checkItemsRef}>
              {checkTrail.map((style, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <animated.div style={style}>
                    <MKBox display="flex" alignItems="center">
                      <AssignmentTurnedInIcon 
                        sx={{ color: "success.main", mr: 1.5, fontSize: 20 }} 
                      />
                      <MKTypography variant="body2" color="text">
                        {checkItems[index]}
                      </MKTypography>
                    </MKBox>
                  </animated.div>
                </Grid>
              ))}
            </Grid>

            {/* Emergency Breakdown Support Section */}
            <AnimatedMKBox 
              ref={emergencyRef}
              style={emergencyAnimation}
              component="section" 
              p={4} 
              borderRadius="lg" 
              mb={5}
              mt={6}
              sx={{ 
                background: "linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(33, 150, 243, 0.1) 100%)"
              }}
            >
              <MKTypography variant="h5" mb={2}>
                Emergency Breakdown Support
              </MKTypography>
              <MKTypography variant="body1" color="text" mb={2}>
                All AMC customers get priority access to our emergency support team. We respond to 
                breakdown calls within 24 hours, with faster response options available based on your plan.
              </MKTypography>
              <MKTypography variant="body1" color="text">
                Our technicians are equipped with genuine spare parts and diagnostic tools to resolve 
                most issues on the first visit. We maintain a comprehensive spare parts inventory for 
                all major brands to ensure minimal downtime.
              </MKTypography>
            </AnimatedMKBox>

            {/* One-Time Services Section */}
            <AnimatedMKBox 
              ref={emergencyRef}
              style={emergencyAnimation}
              component="section" 
              p={4} 
              borderRadius="lg" 
              mb={5}
              sx={{ backgroundColor: "rgba(255, 152, 0, 0.05)" }}
            >
              <MKTypography variant="h5" mb={2} color="warning.main">
                One-Time Services Available
              </MKTypography>
              <MKTypography variant="body1" color="text">
                Not ready for an AMC? We also offer one-time services including panel cleaning, 
                inverter repair, system health audit, performance optimization, component replacement, 
                and troubleshooting. Contact us for customized service packages.
              </MKTypography>
            </AnimatedMKBox>

          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default MaintenanceService;